import { isRetryableError } from './retry';

// KD-3991: the API allows 300 requests a minute per user, counted in fixed
// one-minute windows. A page opened in a busy minute gets 429s: its reads wait
// and go again instead of failing. Writes are never replayed.
const MAX_REPLAYS = 2;
// Without a readable Retry-After (a CORS header the API may not expose).
const FALLBACK_DELAYS_MS = [2000, 5000];
// A window resetting later than this is not worth the wait: the request fails
// and the page offers to try again.
const MAX_WAIT_MS = 10000;
// Every refused request of a page replays at the same moment otherwise.
const JITTER_MS = 1000;

export const isRateLimitError = (error) => error?.response?.status === 429;

// An API busy, down or out of reach: worth trying again, not a reason to end
// the session.
export const isTransientError = (error) =>
  Boolean(error) && (isRateLimitError(error) || isRetryableError(error));

const waitFor = (error, replays) => {
  const retryAfter = Number(error.response.headers?.['retry-after']);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    const wait = retryAfter * 1000;
    return wait <= MAX_WAIT_MS ? wait : null;
  }
  return FALLBACK_DELAYS_MS[replays];
};

/**
 * For a GET refused with 429, the replay through `instance` once the API
 * allows it again; null when the error must reach the caller as is.
 */
export const replayRateLimited = (error, instance) => {
  const request = error?.config;
  if (!isRateLimitError(error) || request?.method !== 'get') return null;

  const replays = request._rateLimitReplays ?? 0;
  const wait = replays < MAX_REPLAYS ? waitFor(error, replays) : null;
  if (wait == null) return null;

  request._rateLimitReplays = replays + 1;
  return new Promise((resolve) =>
    setTimeout(resolve, wait + Math.random() * JITTER_MS),
  ).then(() => instance(request));
};
